/**
 * Admin — Firmalar (BMU staff)
 *
 *   /admin/firmalar
 *
 * Listele + arama + "+ Yeni Firma" modal (firma + owner kullanıcı tek hamlede).
 */
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'wouter';
import { Header } from '../components/Header.js';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Field,
  Input,
} from '../components/ui.js';
import { api, ApiError } from '../lib/api.js';
import { useSession } from '../lib/auth-client.js';

interface OrgRow {
  id: string;
  name: string;
  industry: string;
  teamSize: number | null;
  bmuLeadStatus: string;
  createdAt: string;
  deletedAt: string | null;
  owner: { id: string; email: string; name: string | null } | null;
  _count: { members: number; exams: number };
}

interface CreateOrgResult {
  org: { id: string; name: string };
  owner: { id: string; email: string; name: string };
  initialPassword: string;
  note: string;
}

const INDUSTRIES = [
  { value: 'MACHINERY', label: 'Makine İmalat' },
  { value: 'AUTOMOTIVE', label: 'Otomotiv' },
  { value: 'DEFENSE', label: 'Savunma' },
  { value: 'CONSUMER', label: 'Tüketim Ürünleri' },
  { value: 'SHEET_FAB', label: 'Sac Metal' },
  { value: 'PRECISION_MECHANICAL', label: 'Hassas Mekanik' },
  { value: 'OTHER', label: 'Diğer' },
];

const INDUSTRY_LABELS = Object.fromEntries(
  INDUSTRIES.map((i) => [i.value, i.label]),
);

export function AdminOrgsPage(): JSX.Element {
  const [, setLocation] = useLocation();
  const { data: session, isPending } = useSession();
  const [orgs, setOrgs] = useState<OrgRow[]>([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  async function refresh(): Promise<void> {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (includeDeleted) params.set('includeDeleted', 'true');
      params.set('limit', '100');
      const data = await api.get<{ orgs: OrgRow[]; total: number }>(
        `/api/admin/orgs?${params.toString()}`,
      );
      setOrgs(data.orgs);
      setTotal(data.total);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError('Liste yüklenemedi');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (isPending) return;
    if (!session?.user) {
      setLocation('/yonetici/giris');
      return;
    }
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, isPending, q, includeDeleted]);

  async function deleteOrg(id: string, name: string): Promise<void> {
    if (!confirm(`"${name}" firmasını sil? Soft delete (geri alınabilir).`)) return;
    try {
      await api.delete(`/api/admin/orgs/${id}`);
      await refresh();
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
    }
  }

  async function restoreOrg(id: string): Promise<void> {
    try {
      await api.post(`/api/admin/orgs/${id}/restore`);
      await refresh();
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="mx-auto max-w-6xl px-6 py-12">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-3">
          <div>
            <button
              type="button"
              onClick={() => setLocation('/admin/dashboard')}
              className="text-sm font-medium text-blue-900 hover:underline"
            >
              ← Admin Panel
            </button>
            <h1 className="mt-2 text-3xl font-bold text-slate-900">
              Firma Yönetimi
            </h1>
            <p className="text-sm text-slate-600">
              Tüm firmalar ve owner'ları. Yeni firma kurarken owner kullanıcı
              da otomatik yaratılır.
            </p>
          </div>
          <Button onClick={() => setShowCreate(true)}>+ Yeni Firma</Button>
        </div>

        {error && (
          <div className="mb-4">
            <Alert variant="danger">{error}</Alert>
          </div>
        )}

        {showCreate && (
          <CreateOrgModal
            onClose={() => setShowCreate(false)}
            onCreated={async () => {
              setShowCreate(false);
              await refresh();
            }}
          />
        )}

        <Card className="mb-6">
          <div className="flex flex-wrap items-center gap-3">
            <Input
              type="text"
              placeholder="Firma adı ara…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="max-w-sm"
            />
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={includeDeleted}
                onChange={(e) => setIncludeDeleted(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300"
              />
              Silinenleri göster
            </label>
            <span className="ml-auto text-sm text-slate-500">
              {total} firma
            </span>
          </div>
        </Card>

        {loading ? (
          <p className="text-slate-500">Yükleniyor…</p>
        ) : orgs.length === 0 ? (
          <Card>
            <p className="text-center text-slate-500">Henüz firma yok.</p>
          </Card>
        ) : (
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                    <th className="py-2 pr-3">Firma</th>
                    <th className="py-2 pr-3">Sektör</th>
                    <th className="py-2 pr-3">Owner</th>
                    <th className="py-2 pr-3 text-right">Üye</th>
                    <th className="py-2 pr-3 text-right">Sınav</th>
                    <th className="py-2 pr-3">Lead</th>
                    <th className="py-2 pl-3 text-right">Aksiyon</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {orgs.map((o) => (
                    <tr
                      key={o.id}
                      className={o.deletedAt ? 'opacity-50' : ''}
                    >
                      <td className="py-2 pr-3">
                        <Link
                          href={`/admin/firmalar/${o.id}`}
                          className="font-medium text-slate-900 hover:text-blue-900 hover:underline"
                        >
                          {o.name}
                        </Link>
                        {o.deletedAt && (
                          <span className="ml-2 rounded-full bg-red-50 px-2 py-0.5 text-xs text-red-700">
                            silinmiş
                          </span>
                        )}
                        <p className="text-xs text-slate-500">
                          {new Date(o.createdAt).toLocaleDateString('tr-TR')}
                        </p>
                      </td>
                      <td className="py-2 pr-3 text-slate-700">
                        {INDUSTRY_LABELS[o.industry] ?? o.industry}
                      </td>
                      <td className="py-2 pr-3 text-slate-700">
                        {o.owner ? (
                          <>
                            <p className="text-sm">{o.owner.name ?? '—'}</p>
                            <p className="text-xs text-slate-500">
                              {o.owner.email}
                            </p>
                          </>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-right font-mono">
                        {o._count.members}
                      </td>
                      <td className="py-2 pr-3 text-right font-mono">
                        {o._count.exams}
                      </td>
                      <td className="py-2 pr-3">
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
                          {o.bmuLeadStatus}
                        </span>
                      </td>
                      <td className="py-2 pl-3 text-right">
                        {o.deletedAt ? (
                          <button
                            type="button"
                            onClick={() => restoreOrg(o.id)}
                            className="rounded-md border border-green-300 px-2 py-1 text-xs text-green-700 hover:bg-green-50"
                          >
                            Geri al
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => deleteOrg(o.id, o.name)}
                            className="rounded-md border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50"
                          >
                            Sil
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </main>
    </div>
  );
}

// ─── Create Org Modal ───────────────────────────────────────────

function CreateOrgModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void | Promise<void>;
}): JSX.Element {
  const [name, setName] = useState('');
  const [industry, setIndustry] = useState('MACHINERY');
  const [teamSize, setTeamSize] = useState<string>('');
  const [ownerName, setOwnerName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [customPassword, setCustomPassword] = useState(false);
  const [ownerPassword, setOwnerPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CreateOrgResult | null>(null);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        name,
        industry,
        ownerName,
        ownerEmail,
      };
      if (teamSize) body['teamSize'] = Number(teamSize);
      if (customPassword && ownerPassword) body['ownerPassword'] = ownerPassword;
      const data = await api.post<CreateOrgResult>('/api/admin/orgs', body);
      setResult(data);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError('Firma yaratılamadı');
    } finally {
      setSubmitting(false);
    }
  }

  if (result) {
    return (
      <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 p-4">
        <Card className="w-full max-w-lg">
          <Alert variant="success">
            <strong>Firma yaratıldı.</strong> {result.note}
          </Alert>
          <div className="mt-4 space-y-2 text-sm">
            <Row label="Firma" value={result.org.name} />
            <Row label="Owner Email" value={result.owner.email} mono />
            <Row label="Owner İsim" value={result.owner.name} />
            <div className="rounded-lg border-2 border-amber-400 bg-amber-50 p-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-amber-900">
                Giriş Şifresi (tek seferlik)
              </p>
              <p className="mt-1 font-mono text-base text-slate-900">
                {result.initialPassword}
              </p>
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard.writeText(result.initialPassword);
                }}
                className="mt-2 rounded-md border border-amber-400 px-3 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100"
              >
                Kopyala
              </button>
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <Button
              onClick={async () => {
                await onCreated();
              }}
            >
              Tamamla
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 p-4">
      <Card className="w-full max-w-lg">
        <h3 className="text-lg font-semibold text-slate-900">Yeni Firma</h3>
        <p className="mt-1 text-sm text-slate-600">
          Firma ve owner kullanıcı tek hamlede yaratılır. Şifre otomatik
          üretilir veya manuel girilir.
        </p>

        {error && (
          <div className="mt-3">
            <Alert variant="danger">{error}</Alert>
          </div>
        )}

        <form onSubmit={submit} className="mt-4" noValidate>
          <Field label="Firma Adı" htmlFor="o-name" required>
            <Input
              id="o-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </Field>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Sektör" htmlFor="o-ind" required>
              <select
                id="o-ind"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                className="h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-base focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {INDUSTRIES.map((i) => (
                  <option key={i.value} value={i.value}>
                    {i.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Ekip Büyüklüğü" htmlFor="o-team">
              <Input
                id="o-team"
                type="number"
                min={1}
                max={10000}
                value={teamSize}
                onChange={(e) => setTeamSize(e.target.value)}
              />
            </Field>
          </div>

          <div className="mt-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
            Owner kullanıcı bu firmanın yöneticisi olur. KVKK onayı otomatik
            (BMU staff tarafından yaratıldı).
          </div>

          <Field label="Owner Adı" htmlFor="o-owner-name" required>
            <Input
              id="o-owner-name"
              type="text"
              value={ownerName}
              onChange={(e) => setOwnerName(e.target.value)}
              required
            />
          </Field>

          <Field label="Owner E-posta" htmlFor="o-owner-email" required>
            <Input
              id="o-owner-email"
              type="email"
              value={ownerEmail}
              onChange={(e) => setOwnerEmail(e.target.value.trim().toLowerCase())}
              required
            />
          </Field>

          <Checkbox
            id="o-custom-pw"
            label="Şifreyi manuel gireyim (boş bırak → auto-generate)"
            checked={customPassword}
            onChange={(e) => setCustomPassword(e.target.checked)}
          />
          {customPassword && (
            <Field
              label="Şifre"
              htmlFor="o-pw"
              hint="En az 8 karakter, 1 büyük + 1 küçük + 1 rakam"
            >
              <Input
                id="o-pw"
                type="text"
                value={ownerPassword}
                onChange={(e) => setOwnerPassword(e.target.value)}
              />
            </Field>
          )}

          <div className="mt-4 flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={submitting}
            >
              İptal
            </Button>
            <Button
              type="submit"
              loading={submitting}
              disabled={!name || !ownerName || !ownerEmail}
            >
              Oluştur
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

function Row({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between border-b border-slate-100 py-1">
      <span className="text-xs uppercase tracking-wider text-slate-500">
        {label}
      </span>
      <span
        className={`text-sm text-slate-900 ${mono ? 'font-mono' : ''}`}
      >
        {value}
      </span>
    </div>
  );
}
